import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Progress,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useAuthStore } from '@/store/authStore';
import { useUsers } from '@/hooks/useUsers';
import { useCurrency } from '@/contexts/CurrencyContext';
import { listSalesTeams } from '@/services/salesTeam.service';
import salesTargetService, {
  SALES_TARGET_METRICS,
  type SalesTarget,
  type SalesTargetInput,
  type SalesTargetPeriodType,
} from '@/services/salesTarget.service';
import { crmPersonName } from '@/components/crm/activities/CrmActivityTimeline';
import { formatNumber } from '@/utils/format/number';
import { formatPercent } from '@/utils/format/percent';
import { formatDate } from '@/utils/format/date';

const { Title, Paragraph, Text } = Typography;

function periodStart(type: SalesTargetPeriodType, d: Dayjs): Dayjs {
  if (type === 'Quarter') return d.month(Math.floor(d.month() / 3) * 3).startOf('month');
  return d.startOf('month');
}

function periodLabel(t: Pick<SalesTarget, 'period_type' | 'period_start'>): string {
  const d = dayjs(t.period_start);
  return t.period_type === 'Quarter' ? `Q${Math.floor(d.month() / 3) + 1} ${d.year()}` : d.format('MMM YYYY');
}

const SalesTargetsPage: React.FC = () => {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { user } = useAuthStore();
  const { formatCurrency } = useCurrency();
  const canManage = Boolean(user?.is_super_admin || user?.permissions?.includes('growth.sales_target.manage'));

  const [periodType, setPeriodType] = useState<SalesTargetPeriodType>('Month');
  const [period, setPeriod] = useState<Dayjs>(dayjs());
  const [editing, setEditing] = useState<SalesTarget | 'new' | null>(null);
  const [form] = Form.useForm();

  const start = periodStart(periodType, period).format('YYYY-MM-DD');
  const { data: targets = [], isLoading } = useQuery({
    queryKey: ['sales-targets', periodType, start],
    queryFn: () => salesTargetService.list({ period_type: periodType, period_start: start }),
  });
  const { data: users = [] } = useUsers({ is_active: true });
  const { data: teams = [] } = useQuery({ queryKey: ['sales-teams'], queryFn: () => listSalesTeams(true) });

  const saveMut = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: SalesTargetInput }) => salesTargetService.save(body, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-targets'] });
      message.success('Target saved');
      setEditing(null);
    },
    onError: (e: unknown) =>
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to save target'
      ),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => salesTargetService.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-targets'] });
      message.success('Target deleted');
    },
  });

  const open = (row: SalesTarget | 'new') => {
    form.resetFields();
    if (row === 'new') {
      form.setFieldsValue({ scope: 'user', period_type: periodType, period: periodStart(periodType, period) });
    } else {
      form.setFieldsValue({
        ...row,
        scope: row.user_id ? 'user' : 'team',
        period: dayjs(row.period_start),
      });
    }
    setEditing(row);
  };

  const submit = async () => {
    const v = await form.validateFields();
    const body: SalesTargetInput = {
      period_type: v.period_type,
      period_start: periodStart(v.period_type, v.period).format('YYYY-MM-DD'),
      user_id: v.scope === 'user' ? v.user_id : null,
      sales_team_id: v.scope === 'team' ? v.sales_team_id : null,
      notes: v.notes ?? null,
    };
    for (const m of SALES_TARGET_METRICS) body[m.field] = v[m.field] ?? null;
    saveMut.mutate({ id: editing === 'new' ? undefined : editing?.id, body });
  };

  const fmt = (money: boolean, n: number) => (money ? formatCurrency(n) : formatNumber(n));

  const columns: ColumnsType<SalesTarget> = useMemo(
    () => [
      {
        title: 'Salesperson / team',
        key: 'scope',
        fixed: 'left',
        width: 200,
        render: (_, r) => (
          <Space direction="vertical" size={0}>
            {r.user_id ? <Text strong>{r.user_name}</Text> : <Text strong>{r.sales_team_name}</Text>}
            <Space size={4}>
              <Tag>{r.user_id ? 'Salesperson' : 'Team'}</Tag>
              <Text type="secondary">{periodLabel(r)}</Text>
            </Space>
          </Space>
        ),
      },
      ...SALES_TARGET_METRICS.map((m) => ({
        title: m.label,
        key: m.field,
        width: m.money ? 180 : 130,
        render: (_: unknown, r: SalesTarget) => {
          const target = r[m.field];
          const actual = Number(r.actuals?.[m.actual] ?? 0);
          if (target == null) {
            return <Text type="secondary">{fmt(m.money, actual)} / —</Text>;
          }
          const pct = target > 0 ? (actual / target) * 100 : 0;
          return (
            <Tooltip title={`${fmt(m.money, actual)} of ${fmt(m.money, target)}`}>
              <div>
                <Text>
                  {fmt(m.money, actual)} <Text type="secondary">/ {fmt(m.money, target)}</Text>
                </Text>
                <Progress
                  percent={Math.min(100, Math.round(pct))}
                  size="small"
                  status={pct >= 100 ? 'success' : 'normal'}
                  format={() => formatPercent(pct, { maximumFractionDigits: 0 })}
                />
              </div>
            </Tooltip>
          );
        },
      })),
      ...(canManage
        ? [
            {
              title: '',
              key: 'actions',
              width: 80,
              fixed: 'right' as const,
              render: (_: unknown, r: SalesTarget) => (
                <Space size={0}>
                  <Button type="text" size="small" icon={<EditOutlined />} onClick={() => open(r)} />
                  <Popconfirm title="Delete this target?" onConfirm={() => deleteMut.mutate(r.id)}>
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ),
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canManage, formatCurrency]
  );

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Sales targets
          </Title>
          <Paragraph type="secondary" style={{ margin: 0 }}>
            Monthly or quarterly targets per salesperson or team, with actuals to date. The weekly performance report
            prorates these targets by day.
          </Paragraph>
        </div>
        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => open('new')}>
            New target
          </Button>
        )}
      </Space>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Radio.Group
            value={periodType}
            onChange={(e) => setPeriodType(e.target.value)}
            options={[
              { label: 'Month', value: 'Month' },
              { label: 'Quarter', value: 'Quarter' },
            ]}
            optionType="button"
          />
          <DatePicker
            picker={periodType === 'Quarter' ? 'quarter' : 'month'}
            value={period}
            allowClear={false}
            onChange={(d) => d && setPeriod(d)}
          />
          <Text type="secondary">
            {formatDate(start)} – {formatDate(periodStart(periodType, period).add(periodType === 'Quarter' ? 3 : 1, 'month').subtract(1, 'day').format('YYYY-MM-DD'))}
          </Text>
        </Space>
      </Card>

      <Card size="small">
        <Table<SalesTarget>
          rowKey="id"
          size="small"
          loading={isLoading}
          columns={columns}
          dataSource={targets}
          pagination={false}
          scroll={{ x: 1400 }}
          locale={{ emptyText: 'No targets set for this period' }}
        />
      </Card>

      <Modal
        open={!!editing}
        title={editing === 'new' ? 'New sales target' : 'Edit sales target'}
        okText="Save"
        onOk={submit}
        onCancel={() => setEditing(null)}
        confirmLoading={saveMut.isPending}
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="scope" label="Target for" rules={[{ required: true }]}>
                <Radio.Group
                  options={[
                    { label: 'Salesperson', value: 'user' },
                    { label: 'Team', value: 'team' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item noStyle shouldUpdate={(a, b) => a.scope !== b.scope}>
                {({ getFieldValue }) =>
                  getFieldValue('scope') === 'team' ? (
                    <Form.Item name="sales_team_id" label="Sales team" rules={[{ required: true }]}>
                      <Select options={teams.map((t) => ({ value: t.id, label: t.team_name }))} />
                    </Form.Item>
                  ) : (
                    <Form.Item name="user_id" label="Salesperson" rules={[{ required: true }]}>
                      <Select
                        showSearch
                        optionFilterProp="label"
                        options={users.map((u) => ({ value: u.id, label: crmPersonName(u) }))}
                      />
                    </Form.Item>
                  )
                }
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="period_type" label="Period" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'Month', label: 'Month' },
                    { value: 'Quarter', label: 'Quarter' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item noStyle shouldUpdate={(a, b) => a.period_type !== b.period_type}>
                {({ getFieldValue }) => (
                  <Form.Item name="period" label="Month / quarter" rules={[{ required: true }]}>
                    <DatePicker
                      picker={getFieldValue('period_type') === 'Quarter' ? 'quarter' : 'month'}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
            {SALES_TARGET_METRICS.map((m) => (
              <Col span={12} key={m.field}>
                <Form.Item name={m.field} label={m.label}>
                  <InputNumber min={0} style={{ width: '100%' }} precision={m.money ? 2 : 0} />
                </Form.Item>
              </Col>
            ))}
            <Col span={24}>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default SalesTargetsPage;
